import { Columns3, RotateCcw, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { AiColumn } from "@/api/deals-api";
import { DEAL_FIELDS, resolveVisibleFields } from "./fields";

/**
 * Which columns the deals table shows.
 *
 * Reads the registry rather than a hardcoded list, so a field added to
 * `fields.ts` appears here without touching this component.
 *
 * Locked fields (name, status) are listed but not toggleable — a row with
 * neither has nothing to identify it or act on.
 *
 * AI columns are listed alongside them, in their own group. They are not
 * registry fields — they are workspace-defined and arrive as data — but from
 * the user's side of the screen they are columns like any other, and before
 * this they were the only ones that could never be switched off.
 */
export function ColumnsMenu({
  visibleColumns,
  onChange,
  onReset,
  aiColumns,
  hiddenAiColumnIds,
  onToggleAiColumn,
}: {
  visibleColumns: string[] | null;
  onChange: (columns: string[]) => void;
  onReset: () => void;
  aiColumns: AiColumn[];
  hiddenAiColumnIds: string[];
  onToggleAiColumn: (columnId: string) => void;
}) {
  const visible = resolveVisibleFields(visibleColumns ?? undefined);
  const visibleIds = new Set(visible.map((f) => f.id));
  const hiddenAi = new Set(hiddenAiColumnIds);
  const hiddenCount =
    DEAL_FIELDS.length -
    visibleIds.size +
    aiColumns.filter((column) => hiddenAi.has(column._id)).length;

  const toggle = (id: string) => {
    const next = visibleIds.has(id)
      ? visible.filter((f) => f.id !== id).map((f) => f.id)
      : // Append in registry order rather than at the end, so turning a column
        // back on puts it where it was, not wherever it was last clicked.
        DEAL_FIELDS.filter(
          (f) => visibleIds.has(f.id) || f.id === id
        ).map((f) => f.id);

    onChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Columns3 className="h-3.5 w-3.5" />
          Columns
          {hiddenCount > 0 && (
            <span className="text-muted-foreground">({hiddenCount} hidden)</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs font-medium">Columns</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-xs text-muted-foreground"
            onClick={onReset}
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
        </div>

        <ScrollArea className="max-h-[320px]">
          <div className="p-1">
            {DEAL_FIELDS.map((field) => {
              const checked = visibleIds.has(field.id);
              return (
                <label
                  key={field.id}
                  className={
                    field.locked
                      ? "flex cursor-not-allowed items-center gap-2.5 rounded-md px-2 py-1.5 opacity-60"
                      : "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted"
                  }
                >
                  <Checkbox
                    checked={checked}
                    disabled={field.locked}
                    onCheckedChange={() => !field.locked && toggle(field.id)}
                    aria-label={`Show ${field.label}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{field.label}</span>
                  </span>
                  {field.locked && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Always
                    </span>
                  )}
                </label>
              );
            })}

            {aiColumns.length > 0 && (
              <>
                <div className="mt-1 border-t px-2 pb-1 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  AI columns
                </div>
                {aiColumns.map((column) => (
                  <label
                    key={column._id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted"
                  >
                    <Checkbox
                      checked={!hiddenAi.has(column._id)}
                      onCheckedChange={() => onToggleAiColumn(column._id)}
                      aria-label={`Show ${column.name}`}
                    />
                    <Sparkles className="h-3 w-3 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">
                        {column.name}
                      </span>
                    </span>
                  </label>
                ))}
              </>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
